import React, { useState } from 'react';
import type { CalibrationReviewPayload } from '../types';
import { cn } from '@core/lib/utils';
import { useChatCopy } from '../../config';

interface CalibrationReviewCardProps {
  entry: CalibrationReviewPayload;
  onApprove: () => void;
  onHold: () => void;
  onDelete: () => void;
  onEdit: (field: 'headline' | 'first_impression', value: string) => void;
  className?: string;
}

export const CalibrationReviewCard = React.memo(function CalibrationReviewCard({
  entry,
  onApprove,
  onHold,
  onDelete,
  onEdit,
  className,
}: CalibrationReviewCardProps) {
  // #1308 (CP-19/§33.1): card strings resolve through the brand copy pack.
  const copy = useChatCopy().cardCopy.calibration;
  const [editingField, setEditingField] = useState<'headline' | 'first_impression' | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (field: 'headline' | 'first_impression') => {
    setEditingField(field);
    setEditValue(field === 'headline' ? entry.headline : entry.first_impression);
  };

  const commitEdit = () => {
    if (editingField) {
      onEdit(editingField, editValue.trim() || (editingField === 'headline' ? entry.headline : entry.first_impression));
      setEditingField(null);
    }
  };

  return (
    <div
      className={cn(
        'my-3 rounded-lg border border-chat-highlight-border bg-chat-highlight-bg p-4 shadow-sm',
        className
      )}
      role="article"
      aria-label={copy.ariaLabel}
    >
      <p className="mb-3 text-caption font-medium uppercase tracking-wide text-chat-highlight">
        {copy.title}
      </p>

      {/* Headline */}
      <div className="mb-2">
        {editingField === 'headline' ? (
          <input
            autoFocus
            className="w-full rounded border border-chat-highlight-border bg-chat-card px-2 py-1 text-body-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-chat-highlight dark:bg-muted"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingField(null); }}
          />
        ) : (
          <button
            className="text-left text-body-sm font-semibold text-foreground hover:underline"
            onClick={() => startEdit('headline')}
            title={copy.clickToEdit}
          >
            {entry.headline}
          </button>
        )}
      </div>

      {/* First impression */}
      <div className="mb-3">
        {editingField === 'first_impression' ? (
          <textarea
            autoFocus
            rows={3}
            className="w-full rounded border border-chat-highlight-border bg-chat-card px-2 py-1 text-body-sm text-foreground focus:outline-none focus:ring-2 focus:ring-chat-highlight dark:bg-muted"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }}
          />
        ) : (
          <button
            className="text-left text-body-sm text-muted-foreground hover:text-foreground hover:underline"
            onClick={() => startEdit('first_impression')}
            title={copy.clickToEdit}
          >
            {entry.first_impression}
          </button>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onApprove}
          className="rounded-md bg-chat-status-success px-3 py-1.5 text-caption font-medium text-chat-on-accent hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-chat-status-success"
        >
          {copy.approve}
        </button>
        <button
          onClick={onHold}
          className="rounded-md bg-chat-status-warning px-3 py-1.5 text-caption font-medium text-chat-on-accent hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-chat-status-warning"
        >
          {copy.hold}
        </button>
        <button
          onClick={onDelete}
          className="rounded-md border border-destructive/40 px-3 py-1.5 text-caption font-medium text-destructive hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-destructive"
        >
          {copy.delete}
        </button>
        <button
          onClick={() => startEdit('headline')}
          className="rounded-md border border-border px-3 py-1.5 text-caption font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {copy.edit}
        </button>
      </div>
    </div>
  );
});
